import { useMemo, useState } from 'react';
import {
  DataTable,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow
} from '@carbon/react';
import _ from 'lodash';

import { useNotifications } from 'core/notifications';
import BusinessUnitModal from 'features/admin/groups/BusinessUnitModal';
import useMutateUpdateBusinessUnit from 'shared/api/business-units/useMutateUpdateBusinessUnit';
import TableToolbarCell from 'shared/components/data-table/TableToolbarCell';
import OverlayLoader from 'shared/components/OverlayLoader';

const BusinessUnits = ({ businessUnits: rawBusinessUnits, onRefreshBusinessUnits, isLoading }) => {
  const { showSuccessNotification, showErrorNotification } = useNotifications();

  const [businessUnitEditIndex, setBusinessUnitEditIndex] = useState(null);

  const updateBusinessUnitMutation = useMutateUpdateBusinessUnit({
    onError: () => showErrorNotification({ description: 'Failed to update business unit' }),
    onSuccess: () => {
      setBusinessUnitEditIndex(null);
      onRefreshBusinessUnits();
      showSuccessNotification({ description: 'Business unit updated successfully' });
    }
  });

  const businessUnits = useMemo(() => _.sortBy(rawBusinessUnits?.slice() ?? [], 'name'), [rawBusinessUnits]);

  const tableData = businessUnits.map((x) => ({
    id: x.id.toString(),
    name: x.name,
    ck: x.ck,
    ct: x.ct,
    ik: x.ik,
    it: x.it
  }));

  const headers = [
    { key: 'name', header: 'Name' },
    { key: 'ck', header: 'Collaborative Knowledge' },
    { key: 'ct', header: 'Collaborative Tactical' },
    { key: 'ik', header: 'Individual Knowledge' },
    { key: 'it', header: 'Individual Tactical' }
  ];

  return (
    <>
      <BusinessUnitModal
        open={businessUnitEditIndex !== null}
        businessUnit={businessUnitEditIndex === null ? null : businessUnits[businessUnitEditIndex]}
        isLoading={updateBusinessUnitMutation.isLoading}
        onClose={() => setBusinessUnitEditIndex(null)}
        onSubmit={(updatedBusinessUnit) =>
          updateBusinessUnitMutation.mutate({
            id: businessUnits[businessUnitEditIndex].id,
            businessUnit: updatedBusinessUnit
          })
        }
      />
      <DataTable
        headers={headers}
        rows={tableData}
        size="sm"
        render={({ rows, getTableContainerProps, getTableProps, getHeaderProps, getRowProps }) => (
          <TableContainer
            title="Groups Administration"
            description="Edit groups in the application"
            {...getTableContainerProps()}
          >
            <OverlayLoader small={rows.length === 0} isLoading={isLoading} />
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableHeader {...getHeaderProps({ header })} key={header.key}>
                      {header.header}
                    </TableHeader>
                  ))}
                  <TableToolbarCell.Header />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow {...getRowProps({ row })} key={row.id}>
                    {row.cells.map((cell) => (
                      <TableCell key={cell.id}>{cell.value}</TableCell>
                    ))}
                    {businessUnits[index]?.isOverride ? (
                      <TableCell />
                    ) : (
                      <TableToolbarCell>
                        <TableToolbarCell.MenuItem itemText="Edit" onClick={() => setBusinessUnitEditIndex(index)} />
                      </TableToolbarCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      />
    </>
  );
};

export default BusinessUnits;
